import { ChangeDetectionStrategy, Component, inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalDismissReasons, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { assertNonNullish, MaybeUndefined } from '../utils';
import { DialogTypes } from './dialogs';
import { NotificationContextAttributesService } from './notifications-context-attributes.service';
import { DialogContextProvider, DialogResult, ModalDialogResponse, NotificationService } from './notifications.service';
import { NotificationState } from './notifications.state';

interface ModalDialog {
  modal: NgbModalRef;
  type: DialogTypes;
}

export class StackedDialogs<T extends NotificationState> {
  public get activeDialog(): MaybeUndefined<T> {
    return this.active.modal.componentInstance as T;
  }

  private get result(): Promise<ModalDialogResponse> {
    assertNonNullish(this.active);

    const { modal, type } = this.active;

    return modal.result
      .then((result: DialogResult) => ({ type, result }))
      .catch((result: ModalDismissReasons | string) => ({ type, result }))
      .finally(() => {
        this.close();
      });
  }

  private modals: ModalDialog[] = [];
  private active: MaybeUndefined<ModalDialog> = undefined;

  public open(type: DialogTypes, modal: NgbModalRef): Promise<ModalDialogResponse> {
    this.modals.push({ modal, type });
    this.active = this.modals.at(-1);

    return this.result;
  }

  private close(): void {
    this.modals.pop();
    this.active = this.modals.at(-1) || undefined;
  }
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent {
  private ngbModal = inject(NgbModal);
  private dialogService = inject(NotificationService);
  private dialogContext = inject(NotificationContextAttributesService);

  private readonly dialogs: StackedDialogs<NotificationState> = new StackedDialogs();

  private get unsavedWarningDialog$(): Observable<boolean> {
    return this.dialogService.dialogSaveData(this.dialogContext.dataUnsavedDialogContext);
  }

  public constructor() {
    this.dialogService.dialogState
      .pipe(
        takeUntilDestroyed(),
        tap((dialog: DialogContextProvider<unknown>) => {
          this.openDialog(dialog)
            .then((response: ModalDialogResponse) => {
              this.dialogService.dialogResponse.next(response);
            })
            .catch((reason: ModalDialogResponse) => {
              this.dialogService.dialogResponse.next(reason);
            });
        }),
      )
      .subscribe();
  }

  private openDialog(context: DialogContextProvider<unknown>): Promise<ModalDialogResponse> {
    return this.dialogs.open(
      context.type,
      this.ngbModal.open(context.component, {
        centered: true,
        beforeDismiss: () => this.unsavedWarning(),
        injector: Injector.create({
          providers: context.provider ? [context.provider] : [],
        }),
      }),
    );
  }

  private unsavedWarning(): Promise<boolean> {
    return firstValueFrom(
      of(void 0).pipe(
        withLatestFrom(this.dialogs.activeDialog?.hasUnsavedChanges$ ?? of(false)),
        switchMap(([_, changed]: [void, boolean]) => {
          return !changed ? of(true) : this.unsavedWarningDialog$.pipe(take(1));
        }),
      ),
    );
  }
}
